import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { CheckCircle, Clock, Shield, Zap, Eye, EyeOff } from 'lucide-react';

const Trial = () => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const isFrench = location.pathname.startsWith('/fr');

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Force language change based on route
  useEffect(() => {
    if (isFrench && i18n.language !== 'fr') {
      i18n.changeLanguage('fr');
    } else if (!isFrench && i18n.language !== 'en') {
      i18n.changeLanguage('en');
    }
  }, [isFrench, i18n]);

  // Check if user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        // Redirect to app if already logged in
        window.location.href = 'https://app.noq5.ai';
      }
    };
    checkSession();
  }, []);

  const signupSchema = z.object({
    name: z.string().trim().min(2, { message: t('trial.validation.nameMin') }).max(100, { message: t('trial.validation.nameMax') }),
    email: z.string().trim().email({ message: t('trial.validation.invalidEmail') }).max(255, { message: t('trial.validation.emailMax') }),
    password: z.string().min(8, { message: t('trial.validation.passwordMin') }).max(128, { message: t('trial.validation.passwordMax') }),
    confirmPassword: z.string()
  }).refine(data => data.password === data.confirmPassword, {
    message: t('trial.validation.passwordMismatch'),
    path: ['confirmPassword']
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = signupSchema.safeParse({ name, email, password, confirmPassword });
    if (!validation.success) {
      toast({
        title: t('trial.toast.validationError'),
        description: validation.error.errors[0].message,
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Redirect to app after email confirmation
      const redirectUrl = 'https://app.noq5.ai';

      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            name: name.trim(),
          }
        }
      });

      if (error) {
        console.error('Signup error:', error);
        
        if (error.message.includes('already registered')) {
          toast({
            title: t('trial.toast.alreadyRegistered'),
            description: t('trial.toast.alreadyRegisteredDesc'),
            variant: "destructive",
          });
        } else {
          toast({
            title: t('trial.toast.signupError'),
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      if (data.user) {
        // Call the welcome email edge function
        try {
          await supabase.functions.invoke('send-welcome-email', {
            body: { 
              email: email.trim(), 
              name: name.trim(),
              locale: isFrench ? 'fr' : 'en'
            }
          });
        } catch (emailError) {
          console.error('Welcome email error:', emailError);
          // Don't fail signup if email fails
        }

        toast({
          title: t('trial.toast.success'),
          description: t('trial.toast.successDesc'),
        });

        // Clear form
        setName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
      }
    } catch (error) {
      console.error('Unexpected signup error:', error);
      toast({
        title: t('trial.toast.signupError'),
        description: t('trial.toast.tryLater'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const baseUrl = isFrench ? 'https://noq5.ai/fr/trial' : 'https://noq5.ai/trial';

  return (
    <Layout>
      <SEOHead
        title={t('trial.seo.title')}
        description={t('trial.seo.description')}
        canonicalUrl={baseUrl}
        hreflangLinks={[
          { lang: 'en', url: 'https://noq5.ai/trial' },
          { lang: 'fr', url: 'https://noq5.ai/fr/trial' },
          { lang: 'x-default', url: 'https://noq5.ai/trial' }
        ]}
      />

      <div className="min-h-screen bg-gradient-to-br from-background via-background to-oli-blue-light/5">
        <div className="container px-4 md:px-6 py-16 md:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Left side - Benefits */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div className="space-y-4">
                <div className="inline-flex items-center rounded-full bg-oli-blue-light/20 px-4 py-2 text-sm font-medium text-oli-blue border border-oli-blue-light/30">
                  <span className="mr-2">🎉</span>
                  {t('trial.badge')}
                </div>
                <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                  {t('trial.title')}
                </h1>
                <p className="text-xl text-muted-foreground">
                  {t('trial.subtitle')}
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/50 border border-white/20 shadow-sm">
                  <div className="w-10 h-10 bg-oli-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-oli-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{t('trial.benefit1.title')}</h3>
                    <p className="text-muted-foreground text-sm">{t('trial.benefit1.desc')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/50 border border-white/20 shadow-sm">
                  <div className="w-10 h-10 bg-oli-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-5 h-5 text-oli-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{t('trial.benefit2.title')}</h3>
                    <p className="text-muted-foreground text-sm">{t('trial.benefit2.desc')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/50 border border-white/20 shadow-sm">
                  <div className="w-10 h-10 bg-oli-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-oli-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{t('trial.benefit3.title')}</h3>
                    <p className="text-muted-foreground text-sm">{t('trial.benefit3.desc')}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 p-4 rounded-xl bg-white/50 border border-white/20 shadow-sm">
                  <div className="w-10 h-10 bg-oli-blue/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-5 h-5 text-oli-blue" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{t('trial.benefit4.title')}</h3>
                    <p className="text-muted-foreground text-sm">{t('trial.benefit4.desc')}</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right side - Signup Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl border border-white/30 overflow-hidden">
                <div className="bg-gradient-to-r from-oli-blue to-oli-blue-dark p-6 text-white">
                  <h2 className="text-2xl font-bold">{t('trial.form.title')}</h2>
                  <p className="text-white/90 text-sm mt-1">{t('trial.form.subtitle')}</p>
                </div>

                <form onSubmit={handleSubmit} className="p-8 space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
                      {t('trial.form.name')}
                    </label>
                    <Input
                      id="name"
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder={t('trial.form.namePlaceholder')}
                      required
                      disabled={isSubmitting}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                      {t('trial.form.email')}
                    </label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('trial.form.emailPlaceholder')}
                      required
                      disabled={isSubmitting}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                      {t('trial.form.password')}
                    </label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('trial.form.passwordPlaceholder')}
                        required
                        disabled={isSubmitting}
                        className="w-full pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">{t('trial.form.passwordHint')}</p>
                  </div>

                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                      {t('trial.form.confirmPassword')}
                    </label>
                    <Input
                      id="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder={t('trial.form.confirmPasswordPlaceholder')}
                      required
                      disabled={isSubmitting}
                      className="w-full"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-oli-blue to-oli-blue-dark hover:from-oli-blue-dark hover:to-oli-blue text-white text-lg py-6 font-semibold rounded-xl"
                  >
                    {isSubmitting ? t('trial.form.submitting') : t('trial.form.submit')}
                  </Button>

                  <p className="text-xs text-center text-muted-foreground">
                    {t('trial.form.terms')}{' '}
                    <a href={isFrench ? '/fr/conditions-utilisation' : '/terms-of-service'} className="text-oli-blue hover:underline">
                      {t('trial.form.termsLink')}
                    </a>{' '}
                    {t('trial.form.and')}{' '}
                    <a href={isFrench ? '/fr/politique-confidentialite' : '/privacy-policy'} className="text-oli-blue hover:underline">
                      {t('trial.form.privacyLink')}
                    </a>
                  </p>
                </form>
              </div>

              <p className="text-center text-sm text-muted-foreground mt-4">
                {t('trial.alreadyHaveAccount')}{' '}
                <a href="https://app.noq5.ai" className="text-oli-blue hover:underline font-medium">
                  {t('trial.signIn')}
                </a>
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Trial;
